/**
 * Routing.
 *
 * `/` is the Cone home. Data opens `/data`, which loads the data workspace
 * (sections live under /app/*).
 *
 * Pages are lazy-loaded: each route's code downloads only when it is first
 * visited, so the landing page doesn't ship the whole kanban board.
 */

import { lazy, Suspense } from "react";
import { Navigate, Route, Routes } from "react-router-dom";

import { AppLayout } from "./components/layout/AppLayout";
import { DATASETS } from "./lib/datasets";

const HomePage = lazy(() =>
  import("./pages/HomePage").then((module) => ({ default: module.HomePage })),
);
const DatasetPage = lazy(() =>
  import("./pages/DatasetPage").then((module) => ({ default: module.DatasetPage })),
);
const TasksPage = lazy(() =>
  import("./pages/TasksPage").then((module) => ({ default: module.TasksPage })),
);

/** Shown for the split second while a page's code downloads. */
function PageLoading() {
  return (
    <div className="flex min-h-40 flex-1 items-center justify-center p-8">
      <span className="size-6 animate-spin rounded-full border-2 border-slate-300 border-t-brand" />
    </div>
  );
}

export function App() {
  return (
    <Suspense fallback={<PageLoading />}>
      <Routes>
        <Route path="/" element={<HomePage />} />
        <Route path="/data" element={<Navigate to="/app/funding" replace />} />

        <Route element={<AppLayout />}>
          <Route path="/app/tasks" element={<TasksPage />} />
          <Route path="/app/tasks/shared/:token" element={<TasksPage />} />
          {DATASETS.map((dataset) => (
            <Route
              key={dataset.id}
              path={dataset.path}
              element={<DatasetPage dataset={dataset} />}
            />
          ))}
        </Route>

        <Route path="/app" element={<Navigate to="/app/funding" replace />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Suspense>
  );
}
